import { GatsbyImage } from 'gatsby-plugin-image';
import PropTypes from 'prop-types';
import React from 'react';

const ContentfulImage = ({
  imageFragment,
  gatsbyImageData = imageFragment?.gatsbyImageData,
  className,
  lazy,
  placeholder = 'blurred',
  useObject = false,
  max700 = false,
  ...rest
}) => {
  if (!imageFragment) return null;

  const contentType = imageFragment.file?.contentType;
  const isSvg = contentType === 'image/svg+xml';

  // SVG handling
  if (isSvg) {
    if (useObject) {
      return (
        <div>
          <object
            aria-label={imageFragment.description || imageFragment.alt || rest?.alt || 'visual'}
            data={imageFragment.file.url}
            type="image/svg+xml"
            className={className}
            alt={imageFragment.description || imageFragment.alt || rest?.alt}
            {...rest}
          />
        </div>
      );
    } else {
      return (
        <img
          loading={lazy ? 'lazy' : 'eager'}
          src={imageFragment.file.url}
          alt={imageFragment.description || imageFragment.alt || imageFragment.title}
          className={className}
          width={imageFragment.width}
          height={imageFragment.height}
          {...rest}
        />
      );
    }
  }

  // For all non-SVG images (including PNGs), use GatsbyImage
  const imageData = max700 ? imageFragment.max700 : gatsbyImageData;
  const altText = imageFragment.description || imageFragment.alt || imageFragment.title || '';
  const loadingStrategy = lazy ? 'lazy' : 'eager';

  // Check if we need fullWidth layout
  const isFullWidth =
    typeof window !== 'undefined' && imageFragment.fullWidth && imageFragment.fullWidth > window.innerWidth;

  return (
    <GatsbyImage
      loading={loadingStrategy}
      data-ot-ignore
      alt={altText}
      image={imageData}
      className={className}
      {...(isFullWidth ? { layout: 'fullWidth' } : { layout: 'constrained' })}
      width={imageFragment.width}
      height={imageFragment.height}
      placeholder={placeholder}
      {...rest}
    />
  );
};

ContentfulImage.propTypes = {
  /** The ContentfulAsset object **/
  imageFragment: PropTypes.object.isRequired,
  /** The specific version of gatsbyImageData to use (when dealing with aliases) **/
  gatsbyImageData: PropTypes.object,
  /** Optional classname to be added to the top level tag **/
  className: PropTypes.string,
  /** Whether to lazy load the image **/
  lazy: PropTypes.bool,
  /** Placeholder type for GatsbyImage **/
  placeholder: PropTypes.string,
  /** Whether to use object tag for SVGs **/
  useObject: PropTypes.bool,
  /** Whether to use max700 version of the image **/
  max700: PropTypes.bool,
};

export default ContentfulImage;
